import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FormField } from '../components/ui/FormField';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { SearchableSelect, SelectOption } from '../components/ui/SearchableSelect';
import { Pagination } from '../components/ui/Pagination';
import { Spinner } from '../components/ui/Spinner';
import { useInitEffect } from '../hooks/useInitEffect';
import { useAdminProductList, ADMIN_PRODUCT_SORT_OPTIONS } from '../hooks/useAdminProductList';
import { useVariationCatalog } from '../hooks/useVariationCatalog';
import { adminService } from '../services/adminService';
import { StoreSellerAdmin, UserByRole, AdminCategory, AdminPriceTable, UpdateCategoryRequest, AdminProductStatus, AdminProduct, AdminCollection, HomeBanner, BannerLinkType } from '../models';
import { AdminProductImage, AdminProductDetail, AdminProductDetailVariantColor, AdminSupplier } from '../models/product';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';
import { formatCurrency } from '../utils/currency';
import { safeImageUrl } from '../utils/url';

// ---------------------------------------------------------------------------
// SectionHeader
// ---------------------------------------------------------------------------

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-text-dark">{title}</h2>
      <p className="text-sm text-text-light mt-0.5">{description}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryPriceTableSection
// ---------------------------------------------------------------------------

function CategoryPriceTableSection() {
  const showToast = useToastStore((s) => s.show);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [priceTables, setPriceTables] = useState<AdminPriceTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [bulkPriceTableId, setBulkPriceTableId] = useState<number | undefined>(undefined);
  const [homeOrderDrafts, setHomeOrderDrafts] = useState<Record<number, string>>({});
  const homeOrderTimerRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catResult, ptResult] = await Promise.all([
        adminService.getCategories(),
        adminService.getPriceTables(),
      ]);
      const sorted = [...catResult.items].sort((a, b) => {
        const orderA = a.homeOrder ?? Infinity;
        const orderB = b.homeOrder ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
      setCategories(sorted);
      setPriceTables(ptResult.items);
    } catch {
      showToast('Erro ao carregar categorias e tabelas de preço.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useInitEffect(() => {
    loadData();
  });

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    return categories.filter((cat) =>
      cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [categories, categorySearch]);

  const priceTableOptions = priceTables.map((pt) => ({
    id: pt.id,
    label: pt.isDefault ? `${pt.name} (Padrão)` : pt.name,
  }));

  const buildCategoryPayload = (
    category: AdminCategory,
    overrides: Partial<UpdateCategoryRequest> = {}
  ): UpdateCategoryRequest => ({
    name: category.name,
    description: category.description,
    active: category.active,
    priceTableId: category.priceTableId,
    showOnHome: category.showOnHome,
    homeOrder: category.homeOrder,
    ...overrides,
  });

  const handlePriceTableChange = async (category: AdminCategory, priceTableId: number | undefined) => {
    const newPriceTableId = priceTableId ?? null;
    if (newPriceTableId === category.priceTableId) return;

    setSavingId(category.id);
    const payload = buildCategoryPayload(category, { priceTableId: newPriceTableId });

    try {
      await adminService.updateCategory(category.id, payload);
      setCategories((prev) =>
        prev.map((c) =>
          c.id === category.id
            ? {
                ...c,
                priceTableId: newPriceTableId,
                priceTableName: priceTables.find((pt) => pt.id === newPriceTableId)?.name ?? null,
              }
            : c
        )
      );
      showToast('Tabela de preço atualizada com sucesso.', 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ??
        err?.response?.data?.message ??
        'Erro ao atualizar tabela de preço.';
      showToast(msg, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleShowOnHomeChange = async (category: AdminCategory, showOnHome: boolean) => {
    setSavingId(category.id);
    const homeOrder = showOnHome ? category.homeOrder : null;
    const payload = buildCategoryPayload(category, { showOnHome, homeOrder });

    try {
      await adminService.updateCategory(category.id, payload);
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, showOnHome, homeOrder } : c)));
      showToast('Categoria atualizada com sucesso.', 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao atualizar categoria.';
      showToast(msg, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const clearHomeOrderDraft = (categoryId: number) => {
    setHomeOrderDrafts((prev) => {
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
  };

  const parseHomeOrderDraft = (draft: string): { value: number | null; valid: boolean } => {
    const trimmed = draft.trim();
    if (trimmed === '') return { value: null, valid: true };
    const parsed = Number(trimmed);
    return { value: parsed, valid: !Number.isNaN(parsed) };
  };

  const saveHomeOrder = async (category: AdminCategory, newHomeOrder: number | null) => {
    if (newHomeOrder === category.homeOrder) {
      clearHomeOrderDraft(category.id);
      return;
    }

    setSavingId(category.id);
    const payload = buildCategoryPayload(category, { homeOrder: newHomeOrder });

    try {
      await adminService.updateCategory(category.id, payload);
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, homeOrder: newHomeOrder } : c)));
      showToast('Categoria atualizada com sucesso.', 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao atualizar categoria.';
      showToast(msg, 'error');
    } finally {
      setSavingId(null);
      clearHomeOrderDraft(category.id);
    }
  };

  const handleHomeOrderChange = (category: AdminCategory, value: string) => {
    setHomeOrderDrafts((prev) => ({ ...prev, [category.id]: value }));
    clearTimeout(homeOrderTimerRef.current[category.id]);
    homeOrderTimerRef.current[category.id] = setTimeout(() => {
      const { value: parsed, valid } = parseHomeOrderDraft(value);
      if (!valid) {
        clearHomeOrderDraft(category.id);
        return;
      }
      saveHomeOrder(category, parsed);
    }, 700);
  };

  const handleHomeOrderBlur = (category: AdminCategory) => {
    const draft = homeOrderDrafts[category.id];
    if (draft === undefined) return;

    clearTimeout(homeOrderTimerRef.current[category.id]);
    const { value: parsed, valid } = parseHomeOrderDraft(draft);
    if (!valid) {
      clearHomeOrderDraft(category.id);
      return;
    }
    saveHomeOrder(category, parsed);
  };

  const defaultPriceTable = priceTables.find((pt) => pt.isDefault);

  const handleSetDefault = async () => {
    if (!defaultPriceTable) {
      showToast('Nenhuma tabela de preço está marcada como padrão.', 'error');
      return;
    }
    const ok = await confirm({
      title: 'Aplicar tabela padrão',
      message: `Definir "${defaultPriceTable.name}" como tabela de preço para todas as categorias?`,
    });
    if (!ok) return;

    setBulkSaving(true);
    try {
      await adminService.bulkSetPriceTable(categories.map((c) => c.id), defaultPriceTable.id);
      setCategories((prev) =>
        prev.map((c) => ({ ...c, priceTableId: defaultPriceTable.id, priceTableName: defaultPriceTable.name }))
      );
      showToast(`Tabela "${defaultPriceTable.name}" aplicada a todas as categorias.`, 'success');
    } catch {
      showToast('Erro ao aplicar tabela padrão.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleApplyToAll = async () => {
    if (bulkPriceTableId === undefined) {
      showToast('Selecione uma tabela de preço primeiro.', 'error');
      return;
    }
    const selected = priceTables.find((pt) => pt.id === bulkPriceTableId);
    if (!selected) return;
    const ok = await confirm({
      title: 'Aplicar tabela de preço',
      message: `Aplicar "${selected.name}" para todas as categorias?`,
    });
    if (!ok) return;

    setBulkSaving(true);
    try {
      await adminService.bulkSetPriceTable(categories.map((c) => c.id), selected.id);
      setCategories((prev) =>
        prev.map((c) => ({ ...c, priceTableId: selected.id, priceTableName: selected.name }))
      );
      showToast(`Tabela "${selected.name}" aplicada a todas as categorias.`, 'success');
    } catch {
      showToast('Erro ao aplicar tabela de preço.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleMarkAllForHome = async () => {
    const ok = await confirm({
      title: 'Exibir todas na home',
      message: 'Marcar todas as categorias para exibir na home?',
    });
    if (!ok) return;

    setBulkSaving(true);
    try {
      await adminService.bulkSetShowOnHome(categories.map((c) => c.id), true);
      setCategories((prev) => prev.map((c) => ({ ...c, showOnHome: true })));
      showToast('Todas as categorias foram marcadas para exibir na home.', 'success');
    } catch {
      showToast('Erro ao marcar categorias para a home.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleUnmarkAllFromHome = async () => {
    const ok = await confirm({
      title: 'Ocultar todas da home',
      message: 'Desmarcar todas as categorias da home?',
    });
    if (!ok) return;

    setBulkSaving(true);
    try {
      await adminService.bulkSetShowOnHome(categories.map((c) => c.id), false);
      setCategories((prev) => prev.map((c) => ({ ...c, showOnHome: false, homeOrder: null })));
      showToast('Todas as categorias foram desmarcadas da home.', 'success');
    } catch {
      showToast('Erro ao desmarcar categorias da home.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <>
    {ConfirmDialogElement}
    <section>
      <SectionHeader
        title="Categorias: Preço e Home"
        description="Configure a tabela de preço, a visibilidade e a ordem de cada categoria na home da loja."
      />

      {/* Ações em lote */}
      {!loading && categories.length > 0 && (
        <div className="mb-5 p-4 bg-champagne-light rounded-xl border border-champagne">
          <p className="text-xs font-medium text-text-medium mb-3">Ações em lote</p>
          <div className="flex flex-col sm:flex-row gap-3">
            {defaultPriceTable && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleSetDefault}
                disabled={bulkSaving}
                loading={bulkSaving}
              >
                Aplicar padrão ({defaultPriceTable.name}) para todas
              </Button>
            )}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 max-w-xs">
                <SearchableSelect
                  options={priceTableOptions}
                  value={bulkPriceTableId}
                  onChange={(id) => setBulkPriceTableId(id)}
                  placeholder="Selecionar tabela..."
                  allLabel="Nenhuma"
                />
              </div>
              <Button
                size="sm"
                onClick={handleApplyToAll}
                disabled={bulkSaving || bulkPriceTableId === undefined}
                loading={bulkSaving}
              >
                Aplicar para todas
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleMarkAllForHome}
              disabled={bulkSaving}
              loading={bulkSaving}
            >
              Marcar todas para home
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleUnmarkAllFromHome}
              disabled={bulkSaving}
              loading={bulkSaving}
            >
              Desmarcar todas da home
            </Button>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
        </svg>
        <input
          type="text"
          placeholder="Filtrar por nome da categoria..."
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
          className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
        />
        {categorySearch && (
          <button
            onClick={() => setCategorySearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="sm:hidden space-y-3">
        {loading ? (
          <div className="text-center py-12 text-text-light text-sm">Carregando...</div>
        ) : filteredCategories.length === 0 ? (
          <EmptyState
            title={categorySearch ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
            description={categorySearch ? 'Tente buscar com outro termo.' : 'Cadastre categorias no painel administrativo para configurar as tabelas de preço.'}
          />
        ) : (
          filteredCategories.map((category) => (
            <div key={category.id} className="border border-champagne rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-text-dark">{category.name}</p>
                <span
                  className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                    category.priceTableId
                      ? 'bg-success/10 text-success'
                      : 'bg-champagne text-text-light'
                  }`}
                >
                  {category.priceTableId ? 'Configurada' : 'Sem tabela'}
                </span>
              </div>

              <div>
                <p className="text-xs text-text-light font-medium mb-1">Tabela de Preço</p>
                <SearchableSelect
                  options={priceTableOptions}
                  value={category.priceTableId ?? undefined}
                  onChange={(id) => handlePriceTableChange(category, id)}
                  placeholder={savingId === category.id ? 'Salvando...' : 'Nenhuma tabela'}
                  allLabel="Nenhuma tabela"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <label className="flex items-center gap-2 text-sm text-text-medium">
                  <input
                    type="checkbox"
                    checked={category.showOnHome}
                    disabled={savingId === category.id}
                    onChange={(e) => handleShowOnHomeChange(category, e.target.checked)}
                    className="w-4 h-4 rounded border-champagne-dark accent-chocolate focus:ring-chocolate/20 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  Exibir na home
                </label>

                <label className="flex items-center gap-2 text-sm text-text-medium">
                  Ordem
                  <input
                    type="number"
                    min={0}
                    value={homeOrderDrafts[category.id] ?? (category.homeOrder ?? '')}
                    disabled={!category.showOnHome || savingId === category.id}
                    onChange={(e) => handleHomeOrderChange(category, e.target.value)}
                    onBlur={() => handleHomeOrderBlur(category)}
                    className="w-16 px-2 py-1.5 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm disabled:bg-champagne-light disabled:text-text-light disabled:cursor-not-allowed"
                  />
                </label>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden sm:block border border-champagne rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium">Categoria</th>
              <th className="text-left px-4 py-3 font-medium">Tabela de Preço</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Exibir na home</th>
              <th className="text-left px-4 py-3 font-medium">Ordem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-text-light text-sm">
                  Carregando...
                </td>
              </tr>
            ) : filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    title={categorySearch ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
                    description={categorySearch ? 'Tente buscar com outro termo.' : 'Cadastre categorias no painel administrativo para configurar as tabelas de preço.'}
                  />
                </td>
              </tr>
            ) : (
              filteredCategories.map((category) => (
                <tr
                  key={category.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-text-dark">{category.name}</td>
                  <td className="px-4 py-3">
                    <div className="max-w-xs">
                      <SearchableSelect
                        options={priceTableOptions}
                        value={category.priceTableId ?? undefined}
                        onChange={(id) => handlePriceTableChange(category, id)}
                        placeholder={savingId === category.id ? 'Salvando...' : 'Nenhuma tabela'}
                        allLabel="Nenhuma tabela"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        category.priceTableId
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {category.priceTableId ? 'Configurada' : 'Sem tabela'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={category.showOnHome}
                      disabled={savingId === category.id}
                      onChange={(e) => handleShowOnHomeChange(category, e.target.checked)}
                      className="w-4 h-4 rounded border-champagne-dark accent-chocolate focus:ring-chocolate/20 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={0}
                      value={homeOrderDrafts[category.id] ?? (category.homeOrder ?? '')}
                      disabled={!category.showOnHome || savingId === category.id}
                      onChange={(e) => handleHomeOrderChange(category, e.target.value)}
                      onBlur={() => handleHomeOrderBlur(category)}
                      className="w-20 px-2 py-1.5 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm disabled:bg-champagne-light disabled:text-text-light disabled:cursor-not-allowed"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// SellersSection
// ---------------------------------------------------------------------------

const SELLER_ROLE_ID = 2;

function SellersSection() {
  const showToast = useToastStore((s) => s.show);
  const [sellers, setSellers] = useState<StoreSellerAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sellerUsers, setSellerUsers] = useState<UserByRole[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSellers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getSellers({ page: 1, pageSize: 50 });
      setSellers(result.items);
    } catch (err) {
      console.error('Erro ao carregar vendedores:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useInitEffect(() => {
    loadSellers();
    adminService.getUsersByRole(SELLER_ROLE_ID)
      .then(setSellerUsers)
      .catch(() => {});
  });

  const handleToggleForm = () => {
    setShowForm((prev) => !prev);
    setSelectedUserId(undefined);
    setWhatsAppPhone('');
  };

  const handleUserSelect = (id: number | undefined) => {
    setSelectedUserId(id);
    if (id !== undefined) {
      const user = sellerUsers.find((u) => u.id === id);
      if (user?.phone) setWhatsAppPhone(formatPhoneMask(user.phone));
    } else {
      setWhatsAppPhone('');
    }
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedUserId || !whatsAppPhone.trim()) return;

    setSaving(true);
    try {
      await adminService.createSeller({
        userId: selectedUserId,
        whatsAppPhone: unmaskPhone(whatsAppPhone),
      });
      showToast('Vendedor adicionado com sucesso.', 'success');
      setShowForm(false);
      setSelectedUserId(undefined);
      setWhatsAppPhone('');
      await loadSellers();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao salvar vendedor.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminService.deleteSeller(id);
      setSellers((prev) => prev.filter((s) => s.id !== id));
      showToast('Vendedor removido com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover vendedor.';
      showToast(msg, 'error');
    }
  };

  return (
    <section>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionHeader
          title="Vendedores"
          description="Controle quais vendedores têm acesso ao sistema da loja."
        />
        <Button size="sm" onClick={handleToggleForm}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d={showForm ? 'M6 18L18 6M6 6l12 12' : 'M12 4v16m8-8H4'}
            />
          </svg>
          {showForm ? 'Cancelar' : 'Adicionar vendedor'}
        </Button>
      </div>

      {/* Inline creation form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-5 p-4 bg-champagne-light rounded-xl border border-champagne flex flex-col sm:flex-row gap-3 items-end"
        >
          <FormField label="Vendedor" required>
            <SearchableSelect
              options={sellerUsers.map((u) => ({ id: u.id, label: `${u.name} (${u.email})` }))}
              value={selectedUserId}
              onChange={handleUserSelect}
              placeholder="Selecionar vendedor..."
              allLabel="Nenhum"
            />
          </FormField>
          <FormField label="WhatsApp" required>
            <Input
              type="tel"
              placeholder="(85) 99922-4455"
              value={whatsAppPhone}
              onChange={(e) => setWhatsAppPhone(formatPhoneMask(e.target.value))}
            />
          </FormField>
          <div className="flex gap-2 shrink-0 pb-0.5">
            <Button type="button" variant="ghost" size="sm" onClick={handleToggleForm}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" loading={saving}>
              Salvar
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="border border-champagne rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium">Nome</th>
              <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">E-mail</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">WhatsApp</th>
              <th className="text-left px-4 py-3 font-medium">Ativo</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-text-light text-sm">
                  Carregando...
                </td>
              </tr>
            ) : sellers.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    title="Nenhum vendedor cadastrado"
                    description="Adicione vendedores para que possam acessar o painel da loja."
                  />
                </td>
              </tr>
            ) : (
              sellers.map((seller) => (
                <tr
                  key={seller.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-text-dark">{seller.userName}</td>
                  <td className="px-4 py-3 text-text-medium hidden sm:table-cell">
                    {seller.userEmail}
                  </td>
                  <td className="px-4 py-3 text-text-medium hidden md:table-cell">
                    {seller.whatsAppPhone}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        seller.active
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {seller.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="danger" size="sm" onClick={() => handleDelete(seller.id)}>
                      Remover
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// HomeImagesSection
// ---------------------------------------------------------------------------

interface HomeImageSlotState {
  exists: boolean;
  url: string | null;
  version: number;
}

interface HomeImageGroupItem {
  slot: string;
  label: string;
}

const HOME_IMAGE_FINDER_GROUP: HomeImageGroupItem[] = [
  { slot: 'finder-meninas', label: 'Meninas' },
  { slot: 'finder-meninos', label: 'Meninos' },
  { slot: 'finder-acessorios', label: 'Acessórios' },
];

const HOME_IMAGE_SIZE_GROUP: HomeImageGroupItem[] = [
  { slot: 'size-1-4', label: '1 a 4 anos' },
  { slot: 'size-6-8', label: '6 a 8 anos' },
  { slot: 'size-10-12', label: '10 a 12 anos' },
  { slot: 'size-14', label: '14 anos' },
];

const HOME_IMAGE_INSTAGRAM_GROUP: HomeImageGroupItem[] = [
  { slot: 'instagram-1', label: 'Foto 1' },
  { slot: 'instagram-2', label: 'Foto 2' },
  { slot: 'instagram-3', label: 'Foto 3' },
  { slot: 'instagram-4', label: 'Foto 4' },
  { slot: 'instagram-5', label: 'Foto 5' },
  { slot: 'instagram-6', label: 'Foto 6' },
];

const HOME_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

interface HomeImageCardProps {
  slot: string;
  label: string;
  recommendedSizeLabel: string;
  data?: HomeImageSlotState;
  onChange: (slot: string, data: { exists: boolean; url: string | null }) => void;
}

function HomeImageCard({ slot, label, recommendedSizeLabel, data, onChange }: HomeImageCardProps) {
  const showToast = useToastStore((s) => s.show);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      await adminService.uploadHomeImage(slot, file);
      if (data?.url) {
        onChange(slot, { exists: true, url: data.url });
      } else {
        const fresh = await adminService.getHomeImages();
        const updated = fresh.find((s) => s.key === slot);
        onChange(slot, updated ? { exists: true, url: updated.imageUrl } : { exists: true, url: null });
      }
      showToast('Imagem enviada com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao enviar imagem.';
      showToast(msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Remover imagem',
      message: `Remover a imagem de "${label}"?`,
      confirmLabel: 'Remover',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await adminService.deleteHomeImage(slot);
      onChange(slot, { exists: false, url: null });
      showToast('Imagem removida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover imagem.';
      showToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const displayUrl = data?.exists && data.url ? `${safeImageUrl(data.url)}?t=${data.version}` : null;
  const busy = uploading || deleting;

  return (
    <div className="flex flex-col gap-2">
      {ConfirmDialogElement}
      <div className="relative rounded-xl overflow-hidden border border-champagne bg-champagne-light aspect-[4/5]">
        {displayUrl ? (
          <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-text-light">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[11px]">Nenhuma imagem</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Spinner size="md" />
          </div>
        )}
      </div>

      <span className="text-xs font-medium text-text-dark truncate">{label}</span>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          className="flex-1"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {data?.exists ? 'Substituir' : 'Enviar'}
        </Button>
        {data?.exists && (
          <Button size="sm" variant="danger" disabled={busy} onClick={handleDelete}>
            Remover
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={HOME_IMAGE_ACCEPT}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      <p className="text-[11px] text-text-light">Recomendado: {recommendedSizeLabel}</p>
    </div>
  );
}

function HomeImagesSection() {
  const showToast = useToastStore((s) => s.show);
  const [slots, setSlots] = useState<Record<string, HomeImageSlotState>>({});
  const [loading, setLoading] = useState(false);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getHomeImages();
      const map: Record<string, HomeImageSlotState> = {};
      [...HOME_IMAGE_FINDER_GROUP, ...HOME_IMAGE_SIZE_GROUP, ...HOME_IMAGE_INSTAGRAM_GROUP].forEach((item) => {
        map[item.slot] = { exists: false, url: null, version: Date.now() };
      });
      data.forEach((item) => {
        map[item.key] = { exists: true, url: item.imageUrl, version: Date.now() };
      });
      setSlots(map);
    } catch {
      showToast('Erro ao carregar imagens da home.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useInitEffect(() => {
    loadSlots();
  });

  const handleSlotChange = useCallback((slot: string, data: { exists: boolean; url: string | null }) => {
    setSlots((prev) => ({ ...prev, [slot]: { ...data, version: Date.now() } }));
  }, []);

  return (
    <section>
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <div>
            <SectionHeader
              title="O que você está procurando"
              description="Imagens exibidas na seção de busca rápida por público, logo abaixo do banner principal."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {HOME_IMAGE_FINDER_GROUP.map((item) => (
                <HomeImageCard
                  key={item.slot}
                  slot={item.slot}
                  label={item.label}
                  recommendedSizeLabel="800×1000px (proporção 4:5)"
                  data={slots[item.slot]}
                  onChange={handleSlotChange}
                />
              ))}
            </div>
          </div>

          <div>
            <SectionHeader
              title="Compre por tamanho"
              description="Imagens exibidas na seção de compra por faixa etária."
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {HOME_IMAGE_SIZE_GROUP.map((item) => (
                <HomeImageCard
                  key={item.slot}
                  slot={item.slot}
                  label={item.label}
                  recommendedSizeLabel="800×1000px (proporção 4:5)"
                  data={slots[item.slot]}
                  onChange={handleSlotChange}
                />
              ))}
            </div>
          </div>

          <div>
            <SectionHeader
              title="Instagram"
              description="Fotos exibidas na seção 'Siga a Baby Chic', ao final da home."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {HOME_IMAGE_INSTAGRAM_GROUP.map((item) => (
                <HomeImageCard
                  key={item.slot}
                  slot={item.slot}
                  label={item.label}
                  recommendedSizeLabel="800×800px (proporção 1:1, quadrada)"
                  data={slots[item.slot]}
                  onChange={handleSlotChange}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// CollectionsSection
// ---------------------------------------------------------------------------

const COLLECTION_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

interface CollectionFormState {
  name: string;
  description: string;
  startAt: string;
  endAt: string;
  showInStore: boolean;
  coverImageUrl: string | null;
}

const EMPTY_COLLECTION_FORM: CollectionFormState = {
  name: '',
  description: '',
  startAt: '',
  endAt: '',
  showInStore: true,
  coverImageUrl: null,
};

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toIsoOrNull(dateInputValue: string): string | null {
  if (!dateInputValue) return null;
  return new Date(`${dateInputValue}T00:00:00`).toISOString();
}

function formatCollectionPeriod(collection: AdminCollection): string {
  const start = collection.startAt ? new Date(collection.startAt).toLocaleDateString('pt-BR') : null;
  const end = collection.endAt ? new Date(collection.endAt).toLocaleDateString('pt-BR') : null;
  if (!start && !end) return '—';
  if (start && end) return `${start} – ${end}`;
  return start ? `A partir de ${start}` : `Até ${end}`;
}

function CollectionsSection() {
  const showToast = useToastStore((s) => s.show);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<AdminCollection | null>(null);
  const [form, setForm] = useState<CollectionFormState>(EMPTY_COLLECTION_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [featuringId, setFeaturingId] = useState<number | null>(null);
  const coverFileInputRef = useRef<HTMLInputElement>(null);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getCollections({ pageNumber: 1, pageSize: 100 });
      setCollections(result.items);
    } catch {
      showToast('Erro ao carregar coleções.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useInitEffect(() => {
    loadCollections();
  });

  const handleOpenCreate = () => {
    setEditingCollection(null);
    setForm(EMPTY_COLLECTION_FORM);
    setShowModal(true);
  };

  const handleOpenEdit = (collection: AdminCollection) => {
    setEditingCollection(collection);
    setForm({
      name: collection.name,
      description: collection.description ?? '',
      startAt: toDateInputValue(collection.startAt),
      endAt: toDateInputValue(collection.endAt),
      showInStore: collection.showInStore,
      coverImageUrl: collection.coverImageUrl,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCollection(null);
    setForm(EMPTY_COLLECTION_FORM);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('O nome da coleção é obrigatório.', 'error');
      return;
    }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      startAt: toIsoOrNull(form.startAt),
      endAt: toIsoOrNull(form.endAt),
      showInStore: form.showInStore,
    };

    try {
      if (editingCollection) {
        await adminService.updateCollection(editingCollection.id, payload);
        showToast('Coleção atualizada com sucesso.', 'success');
      } else {
        await adminService.createCollection(payload);
        showToast('Coleção criada com sucesso.', 'success');
      }
      handleCloseModal();
      await loadCollections();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao salvar coleção.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collection: AdminCollection) => {
    const ok = await confirm({
      title: 'Excluir coleção',
      message: `Tem certeza que deseja excluir a coleção "${collection.name}"?`,
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await adminService.deleteCollection(collection.id);
      setCollections((prev) => prev.filter((c) => c.id !== collection.id));
      showToast('Coleção excluída com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao excluir coleção.';
      showToast(msg, 'error');
    }
  };

  const handleSetFeatured = async (collection: AdminCollection) => {
    setFeaturingId(collection.id);
    try {
      await adminService.setCollectionFeaturedInHero(collection.id);
      setCollections((prev) => prev.map((c) => ({ ...c, isFeaturedInHero: c.id === collection.id })));
      showToast(`"${collection.name}" agora está em destaque no carrossel da Home.`, 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao destacar coleção.';
      showToast(msg, 'error');
    } finally {
      setFeaturingId(null);
    }
  };

  const handleCoverFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingCollection) return;
    e.target.value = '';
    setUploadingCover(true);
    try {
      const result = await adminService.uploadCollectionImage(editingCollection.id, file);
      setForm((f) => ({ ...f, coverImageUrl: result.coverImageUrl }));
      setCollections((prev) =>
        prev.map((c) => (c.id === editingCollection.id ? { ...c, coverImageUrl: result.coverImageUrl } : c))
      );
      showToast('Imagem de capa enviada com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao enviar imagem de capa.';
      showToast(msg, 'error');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = async () => {
    if (!editingCollection) return;
    setUploadingCover(true);
    try {
      await adminService.deleteCollectionImage(editingCollection.id);
      setForm((f) => ({ ...f, coverImageUrl: null }));
      setCollections((prev) =>
        prev.map((c) => (c.id === editingCollection.id ? { ...c, coverImageUrl: null } : c))
      );
      showToast('Imagem de capa removida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover imagem de capa.';
      showToast(msg, 'error');
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <>
      {ConfirmDialogElement}
      {showModal && (
        <Modal
          open={showModal}
          onClose={handleCloseModal}
          title={editingCollection ? 'Editar Coleção' : 'Nova Coleção'}
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" form="collection-form" size="sm" loading={saving} disabled={saving}>
                {editingCollection ? 'Salvar' : 'Criar'} coleção
              </Button>
            </div>
          }
        >
          <form id="collection-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
            <FormField label="Nome da coleção" required>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Verão 2026"
              />
            </FormField>

            <FormField label="Descrição">
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descreva a coleção..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Data de início">
                <Input
                  type="date"
                  value={form.startAt}
                  onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
                />
              </FormField>
              <FormField label="Data de fim">
                <Input
                  type="date"
                  value={form.endAt}
                  onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
                />
              </FormField>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-sm text-text-dark cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.showInStore}
                  onChange={(e) => setForm((f) => ({ ...f, showInStore: e.target.checked }))}
                  className="w-4 h-4 rounded border-champagne-dark accent-chocolate focus:ring-chocolate/20 cursor-pointer"
                />
                Mostrar na loja
              </label>
              <p className="text-[11px] text-text-light">
                Exibe esta coleção (com a imagem de capa) como um card no carrossel "Descubra nossas coleções" da home.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-medium">Imagem de capa</span>
              {!editingCollection ? (
                <p className="text-xs text-text-light bg-champagne-light border border-champagne rounded-lg px-3 py-2">
                  A imagem de capa só pode ser adicionada após salvar a coleção.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="relative w-full max-w-[220px] aspect-square rounded-xl overflow-hidden border border-champagne bg-champagne-light">
                    {form.coverImageUrl ? (
                      <img src={safeImageUrl(form.coverImageUrl)} alt="Capa da coleção" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-text-light">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-[11px]">Nenhuma imagem</span>
                      </div>
                    )}
                    {uploadingCover && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Spinner size="md" />
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-text-light">Recomendado: imagem quadrada, mínimo 800×800px (proporção 1:1)</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="flex-1"
                      disabled={uploadingCover}
                      onClick={() => coverFileInputRef.current?.click()}
                    >
                      {form.coverImageUrl ? 'Substituir' : 'Enviar'}
                    </Button>
                    {form.coverImageUrl && (
                      <Button type="button" size="sm" variant="danger" disabled={uploadingCover} onClick={handleRemoveCover}>
                        Remover
                      </Button>
                    )}
                    <input
                      ref={coverFileInputRef}
                      type="file"
                      accept={COLLECTION_IMAGE_ACCEPT}
                      className="hidden"
                      onChange={handleCoverFileChange}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-medium">Destaque no carrossel da Home</span>
              {!editingCollection ? (
                <p className="text-xs text-text-light bg-champagne-light border border-champagne rounded-lg px-3 py-2">
                  O destaque só pode ser definido após salvar a coleção.
                </p>
              ) : editingCollection.isFeaturedInHero ? (
                <div className="flex flex-col gap-1.5">
                  <span className="inline-flex items-center gap-1.5 self-start text-xs font-medium px-2.5 py-1 rounded-full bg-success/10 text-success">
                    Em destaque no Hero
                  </span>
                  <p className="text-[11px] text-text-light">
                    As fotos dos produtos desta coleção estão aparecendo no carrossel "Novidades da coleção", no banner do topo da home (não a imagem de capa).
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-start"
                    loading={featuringId === editingCollection.id}
                    disabled={featuringId === editingCollection.id}
                    onClick={() => handleSetFeatured(editingCollection)}
                  >
                    Destacar no carrossel da Home
                  </Button>
                  <p className="text-[11px] text-text-light">
                    Mostra as fotos dos produtos desta coleção no banner do topo da home (não a imagem de capa). Apenas uma coleção por vez — destacar esta remove o destaque da atual.
                  </p>
                </div>
              )}
            </div>
          </form>
        </Modal>
      )}

      <section>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <SectionHeader
            title="Coleções"
            description="Gerencie as coleções sazonais exibidas na loja e o destaque do carrossel da Home."
          />
          <Button size="sm" onClick={handleOpenCreate}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 4v16m8-8H4" />
            </svg>
            Nova coleção
          </Button>
        </div>

        <div className="border border-champagne rounded-xl overflow-hidden mt-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-champagne text-text-medium">
                <th className="text-left px-4 py-3 font-medium w-14 hidden sm:table-cell">Capa</th>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Período</th>
                <th
                  className="text-left px-4 py-3 font-medium hidden sm:table-cell cursor-help"
                  title="Controla se a coleção aparece como card no carrossel 'Descubra nossas coleções' da home, usando a imagem de capa."
                >
                  Loja
                </th>
                <th
                  className="text-left px-4 py-3 font-medium cursor-help"
                  title="Controla qual coleção tem seus produtos exibidos no carrossel 'Novidades da coleção', no banner do topo da home. Só uma por vez."
                >
                  Destaque
                </th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-light text-sm">
                    Carregando...
                  </td>
                </tr>
              ) : collections.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      title="Nenhuma coleção cadastrada"
                      description="Crie coleções para organizar produtos sazonais e destacá-los na Home."
                    />
                  </td>
                </tr>
              ) : (
                collections.map((collection) => (
                  <tr key={collection.id} className="border-t border-champagne hover:bg-champagne-light/50 transition-colors">
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
                        {collection.coverImageUrl ? (
                          <img src={safeImageUrl(collection.coverImageUrl)} alt={collection.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-text-dark">{collection.name}</td>
                    <td className="px-4 py-3 text-text-medium hidden md:table-cell">{formatCollectionPeriod(collection)}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span
                        title="Aparece como card (com a capa) no carrossel 'Descubra nossas coleções' da home."
                        className={`text-xs font-medium px-2 py-0.5 rounded-full cursor-help ${
                          collection.showInStore ? 'bg-success/10 text-success' : 'bg-champagne text-text-light'
                        }`}
                      >
                        {collection.showInStore ? 'Visível' : 'Oculta'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {collection.isFeaturedInHero ? (
                        <span
                          title="Os produtos desta coleção estão no carrossel 'Novidades da coleção', no banner do topo da home."
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-success/10 text-success whitespace-nowrap cursor-help"
                        >
                          Em destaque
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetFeatured(collection)}
                          disabled={featuringId === collection.id}
                          title="Mostra os produtos desta coleção no carrossel 'Novidades da coleção', no banner do topo da home."
                          className="text-xs font-medium px-2 py-0.5 rounded-full border border-champagne-dark text-text-medium hover:border-chocolate hover:text-chocolate transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          {featuringId === collection.id ? 'Destacando...' : 'Destacar'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(collection)}
                          title="Editar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-light hover:text-chocolate hover:bg-champagne transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(collection)}
                          title="Excluir"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-light hover:text-danger hover:bg-danger/10 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// BannersSection
// ---------------------------------------------------------------------------

const BANNER_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

const BANNER_LINK_TYPE_OPTIONS: { value: BannerLinkType; label: string }[] = [
  { value: 0, label: 'Nenhum' },
  { value: 1, label: 'Produto' },
  { value: 2, label: 'Coleção' },
  { value: 3, label: 'URL externa' },
];

interface BannerFormState {
  linkType: BannerLinkType;
  linkTargetId: number | undefined;
  linkUrl: string;
  displayOrder: number;
  isActive: boolean;
  imageUrl: string | null;
}

const EMPTY_BANNER_FORM: BannerFormState = {
  linkType: 0,
  linkTargetId: undefined,
  linkUrl: '',
  displayOrder: 0,
  isActive: true,
  imageUrl: null,
};

function bannerLinkTypeLabel(linkType: BannerLinkType): string {
  return BANNER_LINK_TYPE_OPTIONS.find((opt) => opt.value === linkType)?.label ?? '—';
}

function formatBannerTarget(
  banner: HomeBanner,
  collectionNameById: Record<number, string>,
  productNameById: Record<number, string>
): string {
  switch (banner.linkType) {
    case 1:
      return banner.linkTargetId ? (productNameById[banner.linkTargetId] ?? `Produto #${banner.linkTargetId}`) : '—';
    case 2:
      return banner.linkTargetId ? (collectionNameById[banner.linkTargetId] ?? `Coleção #${banner.linkTargetId}`) : '—';
    case 3:
      return banner.linkUrl ?? '—';
    default:
      return 'Sem link';
  }
}

function BannersSection() {
  const showToast = useToastStore((s) => s.show);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<HomeBanner | null>(null);
  const [form, setForm] = useState<BannerFormState>(EMPTY_BANNER_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bannersResult, collectionsResult, productsResult] = await Promise.all([
        adminService.getBanners(),
        adminService.getCollections({ pageNumber: 1, pageSize: 100 }),
        adminService.getProducts({ pageNumber: 1, pageSize: 500, sortBy: 'name', sortDirection: 'asc' }),
      ]);
      setBanners([...bannersResult].sort((a, b) => a.displayOrder - b.displayOrder));
      setCollections(collectionsResult.items);
      setProducts(productsResult.items);
    } catch {
      showToast('Erro ao carregar banners.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useInitEffect(() => {
    loadData();
  });

  const collectionNameById = useMemo(
    () => Object.fromEntries(collections.map((c) => [c.id, c.name])),
    [collections]
  );
  const productNameById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p.name])),
    [products]
  );
  const productOptions = useMemo(() => products.map((p) => ({ id: p.id, label: p.name })), [products]);
  const collectionOptions = useMemo(() => collections.map((c) => ({ id: c.id, label: c.name })), [collections]);

  const handleOpenCreate = () => {
    setEditingBanner(null);
    setForm(EMPTY_BANNER_FORM);
    setShowModal(true);
  };

  const handleOpenEdit = (banner: HomeBanner) => {
    setEditingBanner(banner);
    setForm({
      linkType: banner.linkType,
      linkTargetId: banner.linkTargetId ?? undefined,
      linkUrl: banner.linkUrl ?? '',
      displayOrder: banner.displayOrder,
      isActive: banner.isActive,
      imageUrl: banner.imageUrl,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setForm(EMPTY_BANNER_FORM);
  };

  const handleLinkTypeChange = (value: BannerLinkType) => {
    setForm((f) => ({ ...f, linkType: value, linkTargetId: undefined, linkUrl: '' }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if ((form.linkType === 1 || form.linkType === 2) && form.linkTargetId === undefined) {
      showToast('Selecione o produto ou a coleção de destino.', 'error');
      return;
    }
    if (form.linkType === 3 && !form.linkUrl.trim()) {
      showToast('Informe a URL externa.', 'error');
      return;
    }

    setSaving(true);
    const payload = {
      linkType: form.linkType,
      linkTargetId: form.linkType === 1 || form.linkType === 2 ? form.linkTargetId ?? null : null,
      linkUrl: form.linkType === 3 ? form.linkUrl.trim() : null,
      displayOrder: form.displayOrder,
      isActive: form.isActive,
    };

    try {
      if (editingBanner) {
        await adminService.updateBanner(editingBanner.id, payload);
        showToast('Banner atualizado com sucesso.', 'success');
      } else {
        await adminService.createBanner(payload);
        showToast('Banner criado com sucesso.', 'success');
      }
      handleCloseModal();
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao salvar banner.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (banner: HomeBanner) => {
    const ok = await confirm({
      title: 'Excluir banner',
      message: 'Tem certeza que deseja excluir este banner?',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await adminService.deleteBanner(banner.id);
      setBanners((prev) => prev.filter((b) => b.id !== banner.id));
      showToast('Banner excluído com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao excluir banner.';
      showToast(msg, 'error');
    }
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingBanner) return;
    e.target.value = '';
    setUploadingImage(true);
    try {
      const result = await adminService.uploadBannerImage(editingBanner.id, file);
      setForm((f) => ({ ...f, imageUrl: result.imageUrl }));
      setBanners((prev) => prev.map((b) => (b.id === editingBanner.id ? { ...b, imageUrl: result.imageUrl } : b)));
      showToast('Imagem enviada com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao enviar imagem.';
      showToast(msg, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!editingBanner) return;
    setUploadingImage(true);
    try {
      await adminService.deleteBannerImage(editingBanner.id);
      setForm((f) => ({ ...f, imageUrl: null }));
      setBanners((prev) => prev.map((b) => (b.id === editingBanner.id ? { ...b, imageUrl: null } : b)));
      showToast('Imagem removida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover imagem.';
      showToast(msg, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <>
      {ConfirmDialogElement}
      {showModal && (
        <Modal
          open={showModal}
          onClose={handleCloseModal}
          title={editingBanner ? 'Editar Banner' : 'Novo Banner'}
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" form="banner-form" size="sm" loading={saving} disabled={saving}>
                {editingBanner ? 'Salvar' : 'Criar'} banner
              </Button>
            </div>
          }
        >
          <form id="banner-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-medium">Imagem do banner</span>
              {!editingBanner ? (
                <p className="text-xs text-text-light bg-champagne-light border border-champagne rounded-lg px-3 py-2">
                  A imagem só pode ser adicionada após salvar o banner.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="relative w-full aspect-[16/5] rounded-xl overflow-hidden border border-champagne bg-champagne-light">
                    {form.imageUrl ? (
                      <img src={safeImageUrl(form.imageUrl)} alt="Banner" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-text-light">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-[11px]">Nenhuma imagem</span>
                      </div>
                    )}
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Spinner size="md" />
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-text-light">
                    Mínimo 1920×900px. É a mesma imagem exibida em dois cortes diferentes (mais estreita no mobile, bem larga no desktop) — mantenha o conteúdo principal (texto, produto) centralizado, pois as bordas são cortadas de forma diferente entre os tamanhos de tela.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="flex-1"
                      disabled={uploadingImage}
                      onClick={() => imageFileInputRef.current?.click()}
                    >
                      {form.imageUrl ? 'Substituir' : 'Enviar'}
                    </Button>
                    {form.imageUrl && (
                      <Button type="button" size="sm" variant="danger" disabled={uploadingImage} onClick={handleRemoveImage}>
                        Remover
                      </Button>
                    )}
                    <input
                      ref={imageFileInputRef}
                      type="file"
                      accept={BANNER_IMAGE_ACCEPT}
                      className="hidden"
                      onChange={handleImageFileChange}
                    />
                  </div>
                </div>
              )}
            </div>

            <FormField label="Tipo de link" required>
              <select
                value={form.linkType}
                onChange={(e) => handleLinkTypeChange(Number(e.target.value) as BannerLinkType)}
                className="w-full px-3 py-2 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
              >
                {BANNER_LINK_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FormField>

            {form.linkType === 1 && (
              <FormField label="Produto" required>
                <SearchableSelect
                  options={productOptions}
                  value={form.linkTargetId}
                  onChange={(id) => setForm((f) => ({ ...f, linkTargetId: id }))}
                  placeholder="Selecionar produto..."
                  allLabel="Nenhum"
                />
              </FormField>
            )}

            {form.linkType === 2 && (
              <FormField label="Coleção" required>
                <SearchableSelect
                  options={collectionOptions}
                  value={form.linkTargetId}
                  onChange={(id) => setForm((f) => ({ ...f, linkTargetId: id }))}
                  placeholder="Selecionar coleção..."
                  allLabel="Nenhuma"
                />
              </FormField>
            )}

            {form.linkType === 3 && (
              <FormField label="URL externa" required>
                <Input
                  value={form.linkUrl}
                  onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </FormField>
            )}

            <FormField label="Ordem de exibição" required>
              <Input
                type="number"
                min={0}
                value={form.displayOrder}
                onChange={(e) => setForm((f) => ({ ...f, displayOrder: Number(e.target.value) }))}
              />
            </FormField>

            <label className="flex items-center gap-2 text-sm text-text-dark cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="w-4 h-4 rounded border-champagne-dark accent-chocolate focus:ring-chocolate/20 cursor-pointer"
              />
              Ativo
            </label>
          </form>
        </Modal>
      )}

      <section>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <SectionHeader
            title="Banners"
            description="Gerencie os banners promocionais exibidos na home da loja."
          />
          <Button size="sm" onClick={handleOpenCreate}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 4v16m8-8H4" />
            </svg>
            Novo banner
          </Button>
        </div>

        <div className="border border-champagne rounded-xl overflow-hidden mt-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-champagne text-text-medium">
                <th className="text-left px-4 py-3 font-medium w-16 hidden sm:table-cell">Imagem</th>
                <th className="text-left px-4 py-3 font-medium">Link</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Ordem</th>
                <th className="text-left px-4 py-3 font-medium">Ativo</th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-text-light text-sm">
                    Carregando...
                  </td>
                </tr>
              ) : banners.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      title="Nenhum banner cadastrado"
                      description="Crie banners promocionais para exibir na home da loja."
                    />
                  </td>
                </tr>
              ) : (
                banners.map((banner) => (
                  <tr key={banner.id} className="border-t border-champagne hover:bg-champagne-light/50 transition-colors">
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="w-14 h-9 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
                        {banner.imageUrl ? (
                          <img src={safeImageUrl(banner.imageUrl)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-text-dark">{bannerLinkTypeLabel(banner.linkType)}</span>
                        <span className="text-text-medium text-xs truncate max-w-xs">
                          {formatBannerTarget(banner, collectionNameById, productNameById)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-medium hidden md:table-cell">{banner.displayOrder}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          banner.isActive ? 'bg-success/10 text-success' : 'bg-champagne text-text-light'
                        }`}
                      >
                        {banner.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(banner)}
                          title="Editar"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-light hover:text-chocolate hover:bg-champagne transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(banner)}
                          title="Excluir"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-light hover:text-danger hover:bg-danger/10 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// ProductImagesModal
// ---------------------------------------------------------------------------

interface ProductImagesModalProps {
  product: AdminProduct;
  onClose: () => void;
  onImagesChanged: (productId: number, images: AdminProductImage[]) => void;
}

function ProductImagesModal({ product, onClose, onImagesChanged }: ProductImagesModalProps) {
  const showToast = useToastStore((s) => s.show);
  const [images, setImages] = useState<AdminProductImage[]>(product.images ?? []);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [productDetail, setProductDetail] = useState<AdminProductDetail | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingColorId, setPendingColorId] = useState<number | ''>('');
  const [colorTargetImageId, setColorTargetImageId] = useState<number | null>(null);
  const [targetColorId, setTargetColorId] = useState<number | ''>('');
  const [savingColor, setSavingColor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onImagesChangedRef = useRef(onImagesChanged);
  onImagesChangedRef.current = onImagesChanged;

  const loadImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const data = await adminService.getProductImages(product.id);
      setImages(data);
      onImagesChangedRef.current(product.id, data);
    } catch {
      showToast('Erro ao carregar imagens.', 'error');
    } finally {
      setLoadingImages(false);
    }
  }, [product.id, showToast]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    adminService.getProductById(product.id).then(setProductDetail).catch(() => {});
  }, [product.id]);

  const inStockColors = useMemo<AdminProductDetailVariantColor[]>(() => {
    if (!productDetail) return [];
    const colors: AdminProductDetailVariantColor[] = [];
    const seen = new Set<number>();
    for (const variant of productDetail.variants) {
      if (variant.stockQuantity <= 0) continue;
      if (variant.color && !seen.has(variant.color.id)) {
        seen.add(variant.color.id);
        colors.push(variant.color);
      }
    }
    return colors;
  }, [productDetail]);

  const hasInStockColors = inStockColors.length > 0;

  const colorOptions = useMemo(
    () => inStockColors.map((c) => ({ id: c.id, label: c.name })),
    [inStockColors]
  );

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const uploadFile = async (file: File, colorId: number | null) => {
    setUploading(true);
    try {
      await adminService.uploadProductImage(product.id, file, colorId);
      await loadImages();
      showToast('Imagem enviada com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao fazer upload da imagem.';
      showToast(msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (hasInStockColors) {
      setPendingFile(file);
      setPendingColorId('');
      return;
    }
    await uploadFile(file, null);
  };

  const handleConfirmPendingUpload = async () => {
    if (!pendingFile || pendingColorId === '') return;
    const file = pendingFile;
    const colorId = pendingColorId;
    setPendingFile(null);
    setPendingColorId('');
    await uploadFile(file, colorId);
  };

  const handleCancelPendingUpload = () => {
    setPendingFile(null);
    setPendingColorId('');
  };

  const handleOpenColorPicker = (imageId: number, currentColorId: number | null) => {
    setColorTargetImageId(imageId);
    setTargetColorId(currentColorId ?? '');
  };

  const handleCancelColorPicker = () => {
    setColorTargetImageId(null);
    setTargetColorId('');
  };

  const handleConfirmColor = async () => {
    if (colorTargetImageId === null || targetColorId === '') return;
    setSavingColor(true);
    try {
      await adminService.setProductImageColor(product.id, colorTargetImageId, targetColorId);
      setColorTargetImageId(null);
      setTargetColorId('');
      await loadImages();
      showToast('Cor da imagem definida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao definir cor da imagem.';
      showToast(msg, 'error');
    } finally {
      setSavingColor(false);
    }
  };

  const handleSetMain = async (imageId: number) => {
    try {
      await adminService.setMainProductImage(product.id, imageId);
      await loadImages();
      showToast('Imagem principal definida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao definir imagem principal.';
      showToast(msg, 'error');
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    try {
      await adminService.deleteProductImage(product.id, imageId);
      const updated = images.filter((img) => img.id !== imageId);
      setImages(updated);
      onImagesChangedRef.current(product.id, updated);
      showToast('Imagem removida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover imagem.';
      showToast(msg, 'error');
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    const reordered = [...images];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    const withOrder = reordered.map((img, i) => ({ ...img, order: i + 1 }));
    setImages(withOrder);
    try {
      await adminService.reorderProductImages(
        product.id,
        withOrder.map((img) => ({ imageId: img.id, order: img.order }))
      );
      onImagesChangedRef.current(product.id, withOrder);
    } catch {
      showToast('Erro ao reordenar imagens.', 'error');
      await loadImages();
    }
  };

  return (
    /* Overlay */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-champagne flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-chocolate uppercase tracking-wide mb-1">
              Galeria de imagens
            </p>
            <h3 className="text-lg font-semibold text-text-dark truncate">{product.name}</h3>
            <p className="text-xs text-text-light mt-1 font-mono">{product.sku}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-text-light hover:text-chocolate hover:bg-champagne-light transition-colors flex-shrink-0"
            aria-label="Fechar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <p className="text-xs text-text-light bg-champagne-light border border-champagne rounded-lg px-3 py-2 mb-4">
            Recomendado: imagem quadrada (proporção 1:1) — mínimo 1000×1000px, ideal 1600×1600px, já que a imagem principal também é usada na galeria com zoom.
          </p>
          {loadingImages ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : images.length === 0 ? (
            <EmptyState
              title="Nenhuma imagem"
              description="Faça upload para adicionar imagens a este produto."
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {images.map((img, index) => {
                const imageColor = img.colorId !== null
                  ? {
                      id: img.colorId,
                      name: img.colorName ?? '',
                      hexadecimal: img.colorHexadecimal ?? '#000000',
                    }
                  : null;
                const isPickingColor = colorTargetImageId === img.id;
                return (
                  <div
                    key={img.id}
                    className={`relative group rounded-xl overflow-hidden border bg-champagne-light aspect-square ${
                      isPickingColor ? 'border-chocolate ring-2 ring-chocolate' : 'border-champagne'
                    }`}
                  >
                    <img src={safeImageUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                    {img.isMain && (
                      <span className="absolute top-1.5 left-1.5 bg-chocolate text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        Principal
                      </span>
                    )}
                    {hasInStockColors && (
                      <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/60 text-white text-[10px] font-medium pl-1 pr-1.5 py-0.5 rounded-full max-w-[calc(100%-0.75rem)]">
                        {imageColor ? (
                          <>
                            <span
                              className="w-2.5 h-2.5 rounded-full border border-white/60 flex-shrink-0"
                              style={{ backgroundColor: imageColor.hexadecimal }}
                            />
                            <span className="truncate">{imageColor.name}</span>
                          </>
                        ) : (
                          'Sem cor'
                        )}
                      </span>
                    )}
                    {/* Botões de ordem — canto superior direito */}
                    <div className="absolute top-1.5 right-1.5 z-10 flex flex-row gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        disabled={index === 0}
                        onClick={() => handleMove(index, -1)}
                        className="w-6 h-6 rounded bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"
                        title="Mover para esquerda"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        disabled={index === images.length - 1}
                        onClick={() => handleMove(index, 1)}
                        className="w-6 h-6 rounded bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"
                        title="Mover para direita"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      {!img.isMain && (
                        <Button size="sm" variant="secondary" onClick={() => handleSetMain(img.id)}>
                          Definir principal
                        </Button>
                      )}
                      {hasInStockColors && (
                        <Button size="sm" variant="secondary" onClick={() => handleOpenColorPicker(img.id, img.colorId)}>
                          {img.colorId !== null ? 'Alterar cor' : 'Definir cor'}
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => handleDeleteImage(img.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 px-6 py-4 border-t border-champagne flex-shrink-0 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-text-light shrink-0">
            {images.length} {images.length === 1 ? 'imagem' : 'imagens'}
          </span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fechar
            </Button>
            {pendingFile ? (
              <>
                <span className="text-xs text-text-medium truncate max-w-[120px]">{pendingFile.name}</span>
                <div className="w-44">
                  <SearchableSelect
                    options={colorOptions}
                    value={pendingColorId === '' ? undefined : pendingColorId}
                    onChange={(id) => setPendingColorId(id ?? '')}
                    placeholder="Cor da imagem"
                    required
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={handleCancelPendingUpload} disabled={uploading}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  loading={uploading}
                  disabled={uploading || pendingColorId === ''}
                  onClick={handleConfirmPendingUpload}
                >
                  Enviar
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  loading={uploading}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12V4m0 0L8 8m4-4l4 4" />
                  </svg>
                  Fazer upload
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={colorTargetImageId !== null}
        onClose={handleCancelColorPicker}
        title="Cor da imagem"
        footer={
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={handleCancelColorPicker} disabled={savingColor}>
              Cancelar
            </Button>
            <Button
              size="sm"
              loading={savingColor}
              disabled={savingColor || targetColorId === ''}
              onClick={handleConfirmColor}
            >
              Confirmar
            </Button>
          </div>
        }
      >
        <SearchableSelect
          options={colorOptions}
          value={targetColorId === '' ? undefined : targetColorId}
          onChange={(id) => setTargetColorId(id ?? '')}
          placeholder="Selecione a cor"
          required
        />
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductsSection
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { label: string; value: AdminProductStatus }[] = [
  { label: 'Todos', value: 'all' },
  { label: 'Ativos', value: 'active' },
  { label: 'Inativos', value: 'inactive' },
];

const PRODUCT_GENDER_OPTIONS: SelectOption[] = [
  { id: 1, label: 'Masculino' },
  { id: 2, label: 'Feminino' },
  { id: 3, label: 'Outros' },
  { id: 4, label: 'Unissex' },
];

function ProductsSection() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [suppliers, setSuppliers] = useState<AdminSupplier[]>([]);
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  const [imagesModalProduct, setImagesModalProduct] = useState<AdminProduct | null>(null);

  useInitEffect(() => {
    adminService.getCategories().then((data) => setCategories(data.items)).catch(() => {});
    adminService.getSuppliers().then((data) => setSuppliers(data.items)).catch(() => {});
    adminService.getCollections().then((data) => setCollections(data.items)).catch(() => {});
  });

  const {
    products,
    updateProductImages,
    total,
    totalPages,
    isLoading,
    error,
    page,
    searchInput,
    categoryId,
    status,
    sortIndex,
    gender,
    variationTypeId,
    variationValue,
    supplierId,
    collectionId,
    setPage,
    handleSearchChange,
    handleCategoryChange,
    handleStatusChange,
    handleSortChange,
    handleGenderChange,
    handleVariationTypeChange,
    handleVariationValueChange,
    handleSupplierChange,
    handleCollectionChange,
    clearFilters,
    hasFilters,
  } = useAdminProductList();

  const { types: variationTypes } = useVariationCatalog();


  const handleOpenImagesModal = useCallback((product: AdminProduct) => {
    setImagesModalProduct(product);
  }, []);

  const handleCloseImagesModal = useCallback(() => {
    setImagesModalProduct(null);
  }, []);

  const handleImagesChanged = useCallback(
    (productId: number, images: AdminProductImage[]) => {
      updateProductImages(productId, images);
      if (imagesModalProduct?.id === productId) {
        setImagesModalProduct((prev) => prev ? { ...prev, images } : prev);
      }
    },
    [updateProductImages, imagesModalProduct]
  );

  const categoryOptions = categories.map((c) => ({ id: c.id, label: c.name }));
  const supplierOptions = suppliers.map((s) => ({ id: s.id, label: s.name }));
  const collectionOptions = collections.map((c) => ({ id: c.id, label: c.name }));

  const variationTypeOptions: SelectOption[] = variationTypes.map((t) => ({ id: t.id, label: t.name }));
  const variationValueOptions: SelectOption[] =
    variationTypeId !== undefined
      ? variationTypes.find((t) => t.id === variationTypeId)?.options.map((o) => ({ id: o.id, label: o.label })) ?? []
      : [];
  const selectedVariationOption = variationTypeId !== undefined
    ? variationTypes.find((t) => t.id === variationTypeId)?.options.find((o) => o.code === variationValue)
    : undefined;

  const handleVariationValueSelect = (id: number | undefined) => {
    const type = variationTypeId !== undefined ? variationTypes.find((t) => t.id === variationTypeId) : undefined;
    const option = type?.options.find((o) => o.id === id);
    handleVariationValueChange(option?.code);
  };

  return (
    <>
    {imagesModalProduct && (
      <ProductImagesModal
        product={imagesModalProduct}
        onClose={handleCloseImagesModal}
        onImagesChanged={handleImagesChanged}
      />
    )}
    <section>
      <SectionHeader
        title="Produtos"
        description="Visualize e filtre o catálogo de produtos cadastrados no sistema."
      />

      {/* Filters bar */}
      <div className="flex flex-col gap-3 mb-5">
        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por nome ou SKU..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
          />
          {searchInput && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown filters */}
        <div className="flex flex-wrap gap-3">
          {/* Category */}
          <div className="w-full sm:w-52">
            <SearchableSelect
              options={categoryOptions}
              value={categoryId}
              onChange={handleCategoryChange}
              placeholder="Categoria"
              allLabel="Todas as categorias"
            />
          </div>

          {/* Gender */}
          <div className="w-full sm:w-44">
            <SearchableSelect
              options={PRODUCT_GENDER_OPTIONS}
              value={gender}
              onChange={handleGenderChange}
              placeholder="Gênero"
              allLabel="Todos os gêneros"
            />
          </div>

          {/* Variation type */}
          <div className="w-full sm:w-52">
            <SearchableSelect
              options={variationTypeOptions}
              value={variationTypeId}
              onChange={handleVariationTypeChange}
              placeholder="Tipo de variação"
              allLabel="Todos os tipos"
            />
          </div>

          {/* Variation value */}
          {variationTypeId !== undefined && (
            <div className="w-full sm:w-44">
              <SearchableSelect
                options={variationValueOptions}
                value={selectedVariationOption?.id}
                onChange={handleVariationValueSelect}
                placeholder="Valor"
                allLabel="Todos os valores"
              />
            </div>
          )}

          {/* Supplier */}
          <div className="w-full sm:w-52">
            <SearchableSelect
              options={supplierOptions}
              value={supplierId}
              onChange={handleSupplierChange}
              placeholder="Fornecedor"
              allLabel="Todos os fornecedores"
            />
          </div>

          {/* Collection */}
          <div className="w-full sm:w-52">
            <SearchableSelect
              options={collectionOptions}
              value={collectionId}
              onChange={handleCollectionChange}
              placeholder="Coleção"
              allLabel="Todas as coleções"
            />
          </div>

          {/* Sort */}
          <div className="relative w-full sm:w-48">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M6 12h12M10 17h4" />
            </svg>
            <select
              value={sortIndex}
              onChange={handleSortChange}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm appearance-none cursor-pointer"
            >
              {ADMIN_PRODUCT_SORT_OPTIONS.map((opt, idx) => (
                <option key={opt.label} value={idx}>
                  {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Status filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-light font-medium">Status:</span>
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleStatusChange(opt.value)}
                className={[
                  'px-3 py-1 rounded-full text-xs border transition-colors',
                  status === opt.value
                    ? 'bg-champagne border-chocolate text-chocolate font-medium'
                    : 'border-champagne-dark text-text-medium hover:border-chocolate/60 hover:text-text-dark',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Clear filters + count */}
          <div className="flex items-center gap-3">
            {!isLoading && (
              <span className="text-xs text-text-light">
                {total} {total === 1 ? 'produto' : 'produtos'}
              </span>
            )}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-champagne-dark text-xs text-text-medium hover:text-chocolate hover:border-chocolate transition-colors whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-champagne rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium w-12 hidden sm:table-cell">Foto</th>
              <th className="text-left px-4 py-3 font-medium">Produto</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">SKU</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Categoria</th>
              <th className="text-right px-4 py-3 font-medium">Preço</th>
              <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Estoque</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="py-16 text-center">
                  <div className="flex justify-center">
                    <Spinner size="lg" />
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState title="Erro ao carregar produtos" description={error} />
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    title="Nenhum produto encontrado"
                    description={hasFilters ? 'Tente ajustar os filtros.' : 'Nenhum produto cadastrado.'}
                  />
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr
                  key={product.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  {/* Thumbnail */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {(() => {
                      const mainImg = product.images?.find(i => i.isMain)?.imageUrl ?? product.images?.[0]?.imageUrl;
                      return (
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
                          {mainImg ? (
                            <img src={safeImageUrl(mainImg)} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Name */}
                  <td className="px-4 py-3 font-medium text-text-dark max-w-xs">
                    <span className="line-clamp-2">{product.name}</span>
                  </td>

                  {/* SKU */}
                  <td className="px-4 py-3 text-text-medium font-mono text-xs hidden md:table-cell">
                    {product.sku}
                  </td>

                  {/* Category */}
                  <td className="px-4 py-3 text-text-medium hidden lg:table-cell">
                    {product.categoryName}
                  </td>

                  {/* Price */}
                  <td className="px-4 py-3 text-right">
                    {(() => {
                      const defaultPrice = product.productPrices?.find(p => p.isDefault) ?? product.productPrices?.[0];
                      if (!defaultPrice) return <span className="text-text-light text-xs">—</span>;
                      return (
                        <div className="flex flex-col items-end">
                          {defaultPrice.promotionalPrice ? (
                            <>
                              <span className="text-xs text-text-light line-through">
                                R$ {formatCurrency(defaultPrice.price)}
                              </span>
                              <span className="font-semibold text-chocolate">
                                R$ {formatCurrency(defaultPrice.promotionalPrice)}
                              </span>
                            </>
                          ) : (
                            <span className="font-semibold text-text-dark">
                              R$ {formatCurrency(defaultPrice.price)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Stock */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        product.stockCount > 0
                          ? 'bg-success/10 text-success'
                          : 'bg-danger/10 text-danger'
                      }`}
                    >
                      {product.stockCount > 0 ? 'Em estoque' : 'Sem estoque'}
                    </span>
                  </td>

                  {/* Active status */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        product.status === 1
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {product.status === 1 ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleOpenImagesModal(product)}
                      title="Gerenciar imagens"
                      className="relative flex items-center justify-center w-8 h-8 rounded-lg text-text-light hover:text-chocolate hover:bg-champagne transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {(product.imageCount ?? 0) > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center bg-chocolate text-white text-[9px] font-bold rounded-full px-0.5 leading-none">
                          {product.imageCount}
                        </span>
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// ImageSpecsSection
// ---------------------------------------------------------------------------

interface ImageSpecItem {
  name: string;
  location: string;
  ratio: string;
  size: string;
  format: string;
  maxFileSize: string;
}

const IMAGE_SPECS: ImageSpecItem[] = [
  {
    name: 'Banner do topo da home',
    location: 'Banner principal rotativo, no topo da home (aba Banners). A mesma imagem é cortada de forma diferente em mobile e desktop.',
    ratio: '16:10 no mobile, 21:9 a partir do desktop',
    size: 'Mínimo 1920×900px. Conteúdo principal (texto, produto) centralizado, pois as bordas são cortadas de forma diferente entre mobile e desktop.',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: '"O que você está procurando" — Meninas, Meninos, Acessórios',
    location: 'Seção de busca rápida por público, logo abaixo do banner principal (aba Imagens da Home).',
    ratio: '4:5 (retrato)',
    size: '800×1000px',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: '"Compre por tamanho"',
    location: 'Seção de compra por faixa etária (aba Imagens da Home).',
    ratio: '4:5 (retrato)',
    size: '800×1000px',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: 'Instagram (6 fotos)',
    location: 'Seção "Siga a Baby Chic", ao final da home (aba Imagens da Home).',
    ratio: '1:1 (quadrada)',
    size: '800×800px',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: 'Capa de coleção',
    location: 'Card no carrossel "Descubra nossas coleções" da home (aba Coleções).',
    ratio: '1:1 (quadrada)',
    size: 'Mínimo 800×800px',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: 'Imagens de produto',
    location: 'Cards de catálogo, galeria da página do produto e lightbox de zoom (aba Produtos, botão de galeria).',
    ratio: '1:1 (quadrada)',
    size: 'Mínimo 1000×1000px, ideal 1600×1600px (a imagem principal é ampliada no lightbox de zoom)',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
  {
    name: 'Hero — carrossel principal do topo da home',
    location: 'Não tem upload próprio. Mostra automaticamente as fotos de produto (item acima) dos produtos da coleção marcada como "Destacar no carrossel da Home", na aba Coleções.',
    ratio: '4:5 no mobile, 3:4 no desktop',
    size: 'Siga a recomendação de "Imagens de produto" acima',
    format: 'JPEG, PNG ou WEBP',
    maxFileSize: '20MB',
  },
];

function ImageSpecsSection() {
  return (
    <section>
      <SectionHeader
        title="Especificações de imagens"
        description="Tamanho, proporção e formato recomendados para cada imagem cadastrável no site — use como referência rápida antes de fazer upload."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {IMAGE_SPECS.map((item) => (
          <div key={item.name} className="border border-champagne rounded-xl p-4 space-y-2">
            <p className="font-medium text-text-dark">{item.name}</p>
            <p className="text-xs text-text-light">{item.location}</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-1 text-xs">
              <div>
                <p className="text-text-light font-medium mb-0.5">Proporção</p>
                <p className="text-text-dark">{item.ratio}</p>
              </div>
              <div>
                <p className="text-text-light font-medium mb-0.5">Formato aceito</p>
                <p className="text-text-dark">{item.format}</p>
              </div>
              <div className="col-span-2">
                <p className="text-text-light font-medium mb-0.5">Tamanho recomendado</p>
                <p className="text-text-dark">{item.size}</p>
              </div>
              <div>
                <p className="text-text-light font-medium mb-0.5">Arquivo máximo</p>
                <p className="text-text-dark">{item.maxFileSize}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SettingsPage
// ---------------------------------------------------------------------------

interface Tab {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  {
    key: 'products',
    label: 'Produtos',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  {
    key: 'priceTable',
    label: 'Categorias',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'sellers',
    label: 'Vendedores',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    key: 'homeImages',
    label: 'Imagens da Home',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    key: 'collections',
    label: 'Coleções',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 3l9 5-9 5-9-5 9-5zm-9 9l9 5 9-5m-18 5l9 5 9-5" />
      </svg>
    ),
  },
  {
    key: 'banners',
    label: 'Banners',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7zm2 10l4.5-5 3 3.5L16 11l3 6" />
      </svg>
    ),
  },
  {
    key: 'imageSpecs',
    label: 'Especificações de imagens',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].key);

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-dark">Configurações</h1>
        <p className="text-text-light text-sm mt-1">Área restrita para administradores.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm md:flex md:items-stretch overflow-hidden">
        <nav className="flex flex-col gap-1 p-4 border-b border-champagne md:border-b-0 md:border-r md:p-6 md:w-56 md:shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                activeTab === tab.key
                  ? 'bg-champagne text-chocolate'
                  : 'text-text-light hover:text-text-dark hover:bg-champagne-light'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 p-6">
          {activeTab === 'products' && <ProductsSection />}
          {activeTab === 'priceTable' && <CategoryPriceTableSection />}
          {activeTab === 'sellers' && <SellersSection />}
          {activeTab === 'homeImages' && <HomeImagesSection />}
          {activeTab === 'collections' && <CollectionsSection />}
          {activeTab === 'banners' && <BannersSection />}
          {activeTab === 'imageSpecs' && <ImageSpecsSection />}
        </div>
      </div>
    </div>
  );
}
